import { useMemo } from "react"
import { Autocomplete, Stack, TextField, Typography } from "@mui/material"
import { getClientGroups } from "routes/schedule/my-schedules/my-custom-schedules/controller/get-client-trees"
import { assetRegisters } from "routes/asset-register/controller/asset-registers"

export function DataContextSection({ selectedRegime, onRegimeChange, selectedRegister, onRegisterChange }) {
    const clientGroups = getClientGroups.useResults()
    const regimeOptions = useMemo(buildRegimeOptions, [clientGroups])

    const registerResult = assetRegisters.useResults({ skip: 0, take: 100 })
    const registerOptions = useMemo(getRegisterOptions, [registerResult])

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
                Data Context
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Optionally select a regime and/or asset register to populate context-sensitive condition value pickers.
            </Typography>
            <Stack direction="row" spacing={2}>
                <Autocomplete
                    size="small"
                    options={regimeOptions}
                    getOptionLabel={getOptionLabel}
                    value={selectedRegime}
                    onChange={handleRegimeChange}
                    renderInput={renderRegimeInput}
                    isOptionEqualToValue={isOptionEqual}
                    sx={{ flex: 1 }}
                />
                <Autocomplete
                    size="small"
                    options={registerOptions}
                    getOptionLabel={getRegisterLabel}
                    value={selectedRegister}
                    onChange={handleRegisterChange}
                    renderInput={renderRegisterInput}
                    isOptionEqualToValue={isOptionEqual}
                    sx={{ flex: 1 }}
                />
            </Stack>
        </Stack>
    )

    function buildRegimeOptions() {
        return (clientGroups ?? []).flatMap((g) => {
            const entries = []
            if (g.hasLive) {
                entries.push({ id: `live|${g._id}`, label: g.name })
            }
            if (g.hasReady && !g.hasLive) {
                entries.push({ id: `ready|${g._id}`, label: `${g.name} (Ready)` })
            }
            if (!g.hasLive && !g.hasReady) {
                entries.push({ id: g._id, label: g.name })
            }
            return entries
        })
    }

    function getRegisterOptions() {
        return registerResult?.items ?? []
    }

    function getOptionLabel(option) {
        return option?.label || ""
    }

    function getRegisterLabel(option) {
        return option?.name || ""
    }

    function isOptionEqual(option, value) {
        return option?.id === value?.id
    }

    function handleRegimeChange(_, value) {
        onRegimeChange(value)
    }

    function handleRegisterChange(_, value) {
        onRegisterChange(value)
    }

    function renderRegimeInput(params) {
        return <TextField {...params} placeholder="Select a regime..." label="Regime (optional)" />
    }

    function renderRegisterInput(params) {
        return <TextField {...params} placeholder="Select an asset register..." label="Asset Register (optional)" />
    }
}
